import { router } from 'expo-router';
import * as React from 'react';
import { Separator, YStack } from 'smoothie';

import { isEmpty } from '../../../common/misc';
import {
    formatDollars,
    formatMaskedValue,
    formattedDollarsToFloat,
} from '../../../common/utils/bankingUtils';
import { getCadenceDisplayString } from '../../../common/utils/billUtils';
import { formatAsMonthDayYearUTC } from '../../../common/utils/dateUtils';
import type { AccountSummary } from '../../../holistic/types/HolisticSummary';
import type { UpdateBillBody } from '../../hooks/useUpdateBill';
import {
    BillCategory,
    BillCategoryNames,
    BillContext,
    BillCreationType,
    BillEditTypeParam,
    type IBill,
    InlineEditableBillFields,
    type PartialBill,
} from '../../types/Bill';
import BillDetailsRow from './BillDetailsRow';

interface BillDetailsAttributesProps {
    bill: IBill | PartialBill;
    account: AccountSummary;
    context?: BillContext;
    onInlineAttributePress: (fieldName: InlineEditableBillFields) => void;
    onEdit: (name: keyof UpdateBillBody, value: string | number) => void;
}

const BillDetailsAttributes = ({
    bill,
    account,
    context,
    onInlineAttributePress,
    onEdit,
}: BillDetailsAttributesProps) => {
    const {
        payee,
        displayName,
        amount,
        cadence,
        category,
        proposedChanges,
        creationType,
        lastPaidAt,
    } = bill;
    const {
        accountNumberMasked,
        institutionName,
        name: accountName,
    } = account ?? {};

    const getAccountName = (
        accountName: string,
        institutionName: string,
        accountNumberMasked?: string,
    ): string => {
        if (!accountName) {
            return 'Unknown account';
        }
        return `${accountName}\n ${institutionName} ${formatMaskedValue(accountNumberMasked)}`;
    };

    const editRoute =
        context === BillContext.ONBOARDING
            ? '/onboarding/expenses/bill-review-details-edit-new'
            : context === BillContext.REVIEW
              ? '/bills/bill-review-details-edit-new'
              : '/bills/bill-details-new/edit';

    return (
        <YStack separator={<Separator />}>
            <BillDetailsRow
                label={InlineEditableBillFields.PAYEE}
                value={payee ?? 'N/A'}
                onPress={() => {
                    onInlineAttributePress(InlineEditableBillFields.PAYEE);
                }}
                onSubmit={(value) => {
                    onEdit('payee', value);
                }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={true}
            />
            <BillDetailsRow
                label={InlineEditableBillFields.NICKNAME}
                value={displayName ?? 'Add'}
                onPress={() => {
                    onInlineAttributePress(InlineEditableBillFields.NICKNAME);
                }}
                onSubmit={(value) => {
                    onEdit('displayName', value);
                }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={true}
            />
            <BillDetailsRow
                label={InlineEditableBillFields.AMOUNT}
                value={
                    amount != null
                        ? formatDollars(
                              proposedChanges?.averageAmount ?? amount,
                          )
                        : 'N/A'
                }
                type="number"
                onPress={() => {
                    onInlineAttributePress(InlineEditableBillFields.AMOUNT);
                }}
                onSubmit={(value) => {
                    onEdit('amount', formattedDollarsToFloat(value as string));
                }}
                hasProposedChange={proposedChanges?.averageAmount != null}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={true}
            />
            <BillDetailsRow
                label="Frequency"
                value={cadence ?? 'N/A'}
                formattedValue={getCadenceDisplayString(cadence)}
                showCaret={true}
                onPress={() => {
                    if (context === BillContext.ONBOARDING) {
                        router.navigate(
                            `${editRoute}?type=${BillEditTypeParam.FREQUENCY}`,
                        );
                    } else {
                        isEmpty(proposedChanges) &&
                            router.navigate(
                                `${editRoute}?type=${BillEditTypeParam.FREQUENCY}`,
                            );
                    }
                }}
                hasProposedChange={proposedChanges?.frequency != null}
            />
            {(creationType === BillCreationType.MANUAL ||
                creationType === BillCreationType.MONEY_MAPPER) && (
                <BillDetailsRow
                    label="Last paid"
                    value={
                        lastPaidAt != null
                            ? formatAsMonthDayYearUTC(lastPaidAt)
                            : formatAsMonthDayYearUTC(new Date())
                    }
                    placeholder="Select date"
                    showCaret={true}
                    onPress={() => {
                        router.navigate(
                            `/bills/bill-details-new/edit?type=${BillEditTypeParam.LAST_PAYMENT_DATE}`,
                        );
                    }}
                />
            )}
            <BillDetailsRow
                label="Paid from"
                value={getAccountName(
                    accountName,
                    institutionName,
                    accountNumberMasked,
                )}
                showCaret={true}
                onPress={() => {
                    if (context === BillContext.ONBOARDING) {
                        router.navigate(
                            `${editRoute}?type=${BillEditTypeParam.ACCOUNT}`,
                        );
                    } else {
                        isEmpty(proposedChanges) &&
                            router.navigate(
                                `${editRoute}?type=${BillEditTypeParam.ACCOUNT}`,
                            );
                    }
                }}
                hasProposedChange={proposedChanges?.accountId != null}
            />
            <BillDetailsRow
                label="Category"
                value={
                    category != null
                        ? BillCategoryNames[category]
                        : BillCategory.OTHER
                }
                showCaret={true}
                onPress={() => {
                    if (context === BillContext.ONBOARDING) {
                        router.navigate(
                            `${editRoute}?type=${BillEditTypeParam.CATEGORY}`,
                        );
                    } else {
                        isEmpty(proposedChanges) &&
                            router.navigate(
                                `${editRoute}?type=${BillEditTypeParam.CATEGORY}`,
                            );
                    }
                }}
            />
        </YStack>
    );
};

export default BillDetailsAttributes;
